import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isStripeTestMode } from "@/config/stripePlans";
import { toast } from "@/hooks/use-toast";

/**
 * Buys MAC overage in fixed packs via the purchase-mac-overage edge function.
 * The server owns pricing (Growing 100/$75, Premium 100/$60) and clamps to the
 * plan ceiling. `onSuccess` is where the caller should refetch quota state.
 */
export function useMacOveragePurchase(onSuccess?: () => void) {
  const [isPurchasing, setIsPurchasing] = useState(false);

  const purchase = async (packs = 1) => {
    setIsPurchasing(true);
    // Fresh token per click. A resent request (network/proxy retry) reuses it so
    // Stripe dedupes to one charge; a new click gets a new token (real purchase).
    const idempotencyKey =
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    try {
      const { data, error } = await supabase.functions.invoke("purchase-mac-overage", {
        body: { packs_to_purchase: packs, test_mode: isStripeTestMode, idempotency_key: idempotencyKey },
      });
      if (error) {
        // The real error JSON (e.g. ceiling reached) is in error.context.
        let msg = error.message || "Could not purchase overage";
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) msg = body.error;
        } catch {
          // ignore parse errors
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Engaged customers added",
        description: `${data.macs_purchased} engaged customers added for this cycle — S$${data.total_charge_usd} will be added to your next invoice.`,
      });
      onSuccess?.();
      return data;
    } catch (err: any) {
      toast({
        title: "Purchase failed",
        description: err.message || "Could not purchase overage",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsPurchasing(false);
    }
  };

  return { purchase, isPurchasing };
}
